
import { Dialog, DialogActions, DialogContent, DialogTitle, IconButton as MuiIconButton } from '@mui/material';
import { X } from 'lucide-react';
import { Button } from '../ui/button';
import { ReactNode } from 'react';

export interface DialogButton {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
  disabled?: boolean;
  loading?: boolean;
  className?: string;
}

interface GlobalDialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  children: ReactNode;
  buttons?: DialogButton[];
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  fullWidth?: boolean;
  showCloseButton?: boolean;
  dividers?: boolean;
}

const GlobalDialog = ({
  open,
  onClose,
  title,
  children,
  buttons = [],
  maxWidth = 'sm',
  fullWidth = true,
  showCloseButton = true,
  dividers = false
}: GlobalDialogProps) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      scroll="paper"
      PaperProps={{
        sx: {
          borderRadius: 2,
          maxHeight: '90vh',
          m: 2
        }
      }}
    >
      <DialogTitle
        sx={{
          m: 0,
          p: 2.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          borderBottom: dividers ? '1px solid' : 'none',
          borderColor: 'divider',
          flexShrink: 0
        }}
      >
        <span className="text-lg font-semibold">{title}</span>
        {showCloseButton && (
          <MuiIconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'grey.500',
              '&:hover': {
                backgroundColor: 'grey.100'
              }
            }}
          >
            <X size={20} />
          </MuiIconButton>
        )}
      </DialogTitle>

      <DialogContent
        dividers={dividers}
        sx={{
          p: 2.5,
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.3)'
            }
          }
        }}
      >
        {children}
      </DialogContent>

      {buttons.length > 0 && (
        <DialogActions sx={{ p: 2.5, borderTop: dividers ? '1px solid' : 'none', borderColor: 'divider' }}>
          {buttons.map((button, index) => (
            <Button
              key={index}
              variant={button.variant || 'default'}
              onClick={button.onClick}
              disabled={button.disabled || button.loading}
              className={button.className}
            >
              {button.loading ? 'Loading...' : button.label}
            </Button>
          ))}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default GlobalDialog;